import { type ReactElement, useCallback, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { LoadingSpinner } from "widgets";

import { pluralizeWord } from "@/app-utils/string.ts";
import { isLicensed } from "@/redux/modules/config.ts";
import {
  type IDashboardCardSampleTypeStatusList,
  loadDashboardCardSampleTypeStatuses,
} from "@/redux/modules/dashboard.ts";
import { type IAppState } from "@/redux/reducer.ts";
import type { ReduxDispatch } from "@/redux/redux-utils.ts";
import { LICENSE_MODULE } from "@/settings/settings.js";
import { ContentPlaceholder } from "@/ui-modules/core/ContentPlaceholder.tsx";
import { StatisticalTile } from "@/ui-modules/core/DashboardTiles.tsx";
import SampleStatusDashboardCardEditingForm from "@/ui-modules/dashboard/SampleStatusDashboardCardEditingForm.tsx";

import GenericCard, { type IGenericCardProps } from "../core/GenericCard.js";

export interface ISampleStatusesCardConfig {
  sampleTypeId: number;
  statusIds: number[];
}

export const SampleStatusDashboardCard = (props: IGenericCardProps) => {
  const sampleCount = useSelector<IAppState, IDashboardCardSampleTypeStatusList>(
    (state) => state.dashboardState.sampleTypeStatusesCards[props.id],
  );
  const trackerLicensed = useSelector<IAppState, boolean>((state) =>
    isLicensed(state.config.configuration, LICENSE_MODULE.TRACKER),
  );

  const dispatch = useDispatch<ReduxDispatch>();

  const loadSampleTypeStatuses = useCallback(() => {
    const config: ISampleStatusesCardConfig = props.config?.specificConfig as ISampleStatusesCardConfig;
    if (config.sampleTypeId && trackerLicensed) {
      return dispatch(loadDashboardCardSampleTypeStatuses(props.id, config));
    }
    return Promise.resolve();
  }, [dispatch, props.config?.specificConfig, props.id, trackerLicensed]);

  useEffect(() => {
    loadSampleTypeStatuses();
  }, [loadSampleTypeStatuses]);

  const buildSampleFilterExpression = useCallback((status: string): string => {
    return `filter=status.name=in=('${status}')`;
  }, []);

  const primaryParentType = sampleCount?.parentType;
  const sampleType = sampleCount?.sampleType;
  const isLoading: boolean = trackerLicensed && sampleCount?.isLoading;
  const notAvailableMessage = sampleCount?.misconfigured ? (
    <ContentPlaceholder>
      The configured sample type can&#39;t be found. Please review the sample type configuration.
    </ContentPlaceholder>
  ) : null;
  const notConfiguredMessage =
    !sampleType && !sampleCount?.misconfigured ? (
      <ContentPlaceholder>Please configure a valid sample type</ContentPlaceholder>
    ) : null;
  const statusBoard: ReactElement = (
    <>
      {notConfiguredMessage}
      {notAvailableMessage}
      {sampleCount && sampleCount.sampleCounts
        ? sampleCount.sampleCounts.map((sc) => {
            if (sc) {
              const keyTrailer = `${sampleType?.name}-${sc.sampleStatus?.name}`;
              const filterExpression = buildSampleFilterExpression(sc.sampleStatus?.name ?? "");

              return (
                <StatisticalTile
                  key={keyTrailer}
                  time={sc.modified ?? undefined}
                  status={sc.sampleStatus ?? undefined}
                  headStat={{
                    name: "Samples",
                    value: sc.sampleCount ?? 0,
                    link: {
                      pathname: `/sampleType/${sampleCount?.sampleType?.id}/samples`,
                      search: filterExpression,
                    },
                  }}
                  footStat={{
                    name:
                      (primaryParentType && pluralizeWord(primaryParentType.name, sc.parentCount ?? 0)) ?? undefined,
                    value: sc.parentCount ?? 0,
                  }}
                />
              );
            }
            return null;
          })
        : null}
    </>
  );

  return (
    <GenericCard
      {...props}
      refreshData={loadSampleTypeStatuses}
      editingForm={trackerLicensed ? SampleStatusDashboardCardEditingForm : undefined}
      tipTourId={"sample-status-title"}
    >
      {isLoading ? <LoadingSpinner inCenter /> : null}

      <div className="tiles_grid tiles_grid--statistical">
        {!isLoading &&
          (trackerLicensed ? statusBoard : <ContentPlaceholder>Tracker Module is not licensed</ContentPlaceholder>)}
      </div>
    </GenericCard>
  );
};
